import { useTheme } from "@mui/material/styles";
import { Mark, SLACK, SlackMark, parse } from "c8-markdown-parser";
import { grey, red } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import emoji from "emoji-toolkit";
import _ from "lodash";
import React, { PropsWithChildren } from "react";
import { getDisplayableUserName } from "../api";
import SlackContext from "./SlackContext";

type MarkNode = Mark<SlackMark>;

interface EmojiProps {
	shortName: string;
}

const Emoji: React.FunctionComponent<EmojiProps> = ({ shortName }) => {
	const emojiLookup = ":" + shortName + ":";
	return <React.Fragment>{emoji.shortnameToUnicode(emojiLookup) ?? emojiLookup}</React.Fragment>;
};

interface LinkProps extends PropsWithChildren {
	content: string;
}

const Link: React.FunctionComponent<LinkProps> = ({ content, children }) => {
	const [ref, text] = content.split("|", 2);
	if (ref.startsWith("!")) {
		return <a>{text ?? ref}</a>;
	}

	if (ref.startsWith("#")) {
		const channelId = ref.substring(1);
		return <a href={"https://slack.com/app_redirect?channel=" + channelId} target="_blank">{"#" + (text ?? channelId)}</a>;
	}

	if (ref.startsWith("@")) {
		const { usersMap } = React.useContext(SlackContext);
		const userId = ref.substring(1);
		const userName = getDisplayableUserName(usersMap[userId]?.profile);
		return <a>{"@" + userName ?? text ?? userId}</a>;
	}

	if (!!children) {
		// handle the case of url unfurl
		// Slack has this pattern - <http://www.example.com|This message *is* a link>
		// https://api.slack.com/reference/surfaces/formatting
		return (
			<a href={ref} target="_blank">{children}</a>
		);
	}

	return <a href={ref} target="_blank">{text ?? ref}</a>;
};

interface MarkdownProps {
	markdown: string;
}

const Markdown: React.FunctionComponent<MarkdownProps> = ({ markdown }) => {
	const theme = useTheme();
	try {
		const markTree = parse(markdown, SLACK);
		const renderMark = (mark: MarkNode, key: string | number) => {
			switch (mark.name) {
				case "bold": return <b key={key}>{mark.children?.map((m, i) => renderMark(m, i))}</b>;
				case "code-block": return (
					<pre
						key={key}
						style={{
							backgroundColor: grey[200],
							border: STANDARD_BORDER_STYLE,
							borderRadius: "3px",
							marginBottom: theme.spacing(1),
							marginTop: theme.spacing(1),
							padding: theme.spacing(2),
							whiteSpace: "pre-wrap" }}
					><code>
							{mark.content}
						</code>
					</pre>
				);
				case "code-inline": return (
					<code
						key={key}
						style={{
							backgroundColor: grey[200],
							border: STANDARD_BORDER_STYLE,
							borderRadius: "3px",
							color: red[700],
							margin: 0,
							padding: theme.spacing(0.5),
							whiteSpace: "pre-wrap",
						}}
					>
						{mark.content}
					</code>
				);
				case "emoji": return <Emoji key={key} shortName={mark.content} />;
				case "italic": return <i key={key}>{mark.children?.map((m, i) => renderMark(m, i))}</i>;
				case "line-break": return <br key={key} />;
				case "link": return <Link content={mark.content} key={key}>{mark.children?.map((m, i) => renderMark(m, i))}</Link>;
				case "quote": return (
					<blockquote
						key={key}
						style={{
							borderLeft: `4px solid ${grey.A100}`,
							margin: 0,
							paddingLeft: theme.spacing(2),
						}}
					>
						{mark.children?.map((m, i) => renderMark(m, i))}
					</blockquote>
				);
				case "root": return <div key={key}>{mark.children?.map((m, i) => renderMark(m, i))}</div>;
				case "strike": return <span key={key} style={{ textDecoration: "line-through" }}>{mark.children?.map((m, i) => renderMark(m, i))}</span>;
				case "text": return <React.Fragment key={key}>{_.unescape(mark.content)}</React.Fragment>;
			}
			return null;
		};
		return renderMark(markTree, 0);
	} catch (e) {
		console.error("Failed to render Slack markdown " + markdown + "\nException:\n" + e);
		return <span>{markdown}</span>;
	}
};

export default Markdown;
